/**
 * ReactionChips — the reactions on a bubble (PR-4, 2026-09-09; restyled
 * 2026-09-09): one chip per emoji with its count; the viewer's own reactions
 * read as pressed; a click toggles; the tooltip and accessible label name
 * who reacted. Lives at the head of the meta line (MetaLeading — Miles: "be
 * a part of the bottom line of a message"), never in its own row.
 *
 * CONTRAST (Miles, 2026-09-09, screenshot of a gold bubble with a faint
 * translucent circle and a ~10 px emoji: "need more contrast and need to be
 * colorful like they are but somehow need to be seen better"): a solid,
 * near-white pill with a hairline border and a soft shadow reads on the gold
 * user bubble AND on the soft-white Prest0n bubble; the emoji stays the
 * platform's own colourful glyph at a size you can actually see; the count is
 * bold; the viewer's own chip carries a gold outline. Alpha tints and #fff
 * pass the design-token gate.
 */
import React from 'react';
import styled from 'styled-components';
import type { ReactionSummaryEntry } from '../../services/messageReactions/reactionModel';
import { getUserUiLanguage } from '../../services/threadMetadata/uiLanguage';

export interface ReactionChipsProps {
  reactions: ReadonlyArray<ReactionSummaryEntry>;
  /** uid → display name for the tooltip. */
  labelFor: (uid: string) => string;
  onToggle: (emoji: string) => void;
  /** False while the message cannot take a reaction yet (not delivered). */
  canReact?: boolean;
}

export const ReactionChipsGroup = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  user-select: none;
`;

export const ReactionChip = styled.button<{ $mine?: boolean; $canReact?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px 2px 7px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$mine ? 'var(--brand-gold-text)' : 'rgba(0, 0, 0, 0.16)')};
  box-shadow: ${(p) => (p.$mine
    ? '0 0 0 1px var(--brand-gold-text), 0 1px 3px rgba(0, 0, 0, 0.2)'
    : '0 1px 3px rgba(0, 0, 0, 0.2)')};
  background: rgba(255, 255, 255, 0.94);
  color: var(--brand-black);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.8rem;
  line-height: 1.25;
  cursor: ${(p) => (p.$canReact === false ? 'default' : 'pointer')};
  appearance: none;
  -webkit-appearance: none;
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.24);
  }
  &:focus-visible {
    outline: 2px solid var(--brand-gold);
    outline-offset: 1px;
  }
  &:disabled {
    opacity: 0.9;
  }
`;

/** The emoji at a size you can see: the platform's own colourful glyph, never an icon font. */
const Emoji = styled.span`
  font-size: 1.05rem;
  line-height: 1;
`;

const Count = styled.span`
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

export function reactionChipLabel(entry: ReactionSummaryEntry, labelFor: (uid: string) => string, lang: 'en' | 'es'): string {
  const names = entry.uids.map(labelFor).filter(Boolean);
  const who = names.join(', ');
  if (lang === 'es') return `${entry.emoji} ${entry.count} — ${who}`;
  return `${entry.emoji} ${entry.count} — ${who}`;
}

export function ReactionChips({ reactions, labelFor, onToggle, canReact = true }: ReactionChipsProps): React.ReactElement | null {
  if (!reactions || reactions.length === 0) return null;
  const lang = getUserUiLanguage() === 'es' ? 'es' : 'en';
  return (
    <ReactionChipsGroup
      data-reaction-chips=""
      role="group"
      aria-label={lang === 'es' ? 'reacciones' : 'reactions'}
    >
      {reactions.map((entry) => {
        const label = reactionChipLabel(entry, labelFor, lang);
        return (
          <ReactionChip
            key={entry.emoji}
            type="button"
            data-reaction-chip={entry.emoji}
            data-reaction-count={entry.count}
            data-reaction-mine={entry.mine ? 'true' : undefined}
            aria-pressed={entry.mine}
            aria-label={label}
            title={label}
            disabled={!canReact}
            $mine={entry.mine}
            $canReact={canReact}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(entry.emoji);
            }}
          >
            <Emoji aria-hidden="true">{entry.emoji}</Emoji>
            <Count aria-hidden="true">{entry.count}</Count>
          </ReactionChip>
        );
      })}
    </ReactionChipsGroup>
  );
}

export default ReactionChips;
